import { API } from "apis";
import Chip from "components/common/Chip";
import RowsPerPageSelect from "components/common/RowsPerPageSelect";
import Image from "components/common/images/Image";
import PageLabel from "components/common/paging/PageLabel";
import Pagination from "components/common/paging/Pagination2";
import CorpListMainCategory from "components/corp/list/CorpListMainCategory";
import CorpListSideMenu from "components/corp/list/CorpListSideMenu";
import CorpSearchSortSelect from "components/corp/list/CorpSearchSortSelect";
import ContactDialog from "components/dialog/ContactDialog";
import { CategoryType, PER_PAGE_TYPES, PrefectureCode } from "enums";
import { CategoryModel, ContextModel, CorpSearchOptions, PublicCorpListModel, PublicCorpModel } from "models";
import qs from "query-string";
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { Utils } from "utils";

/** 「会社一覧」画面 */
const CorpListPage: React.FC = () => {
  const ctx = useOutletContext<ContextModel>();

  const location = useLocation();
  const navigate = useNavigate();
  /** カテゴリの全情報 */
  const [categoryList, setCategoryList] = React.useState<CategoryModel[]>([]);
  //
  const [keyword, setKeyword] = React.useState("");

  // 直前に検索を行ったクエリ
  const searchedQuery = React.useRef<string>();
  // 検索オプション
  const options = React.useRef<CorpSearchOptions>({});
  // 会社情報
  const [data, setData] = React.useState<PublicCorpListModel>();

  const selectedItem = React.useRef<PublicCorpModel | null>(null);
  const [modalIsOpen, setIsOpen] = React.useState(false);

  const reload = async () => {
    for (let key in options.current) {
      if ((options.current as any)[key] === "") {
        delete (options.current as any)[key];
      }
    }
    const params = qs.stringify(options.current);
    navigate({ pathname: location.pathname, search: params });
  };

  const onChangeLargeCategory = async (categoryId: string) => {
    const cat = categoryList.find((v) => v.id === categoryId);
    if (!cat) {
      return;
    }

    setKeyword("");

    options.current.corp_large_category_id = cat.id;
    delete options.current.corp_medium_category_id;
    delete options.current.prefecture_cd;
    delete options.current.keyword;
    delete options.current.page_no;
    delete options.current.sort_order;
    options.current.get_count = PER_PAGE_TYPES.DEFAULT;

    reload();
  };

  const onChangeMediumCategoryID = async (id?: string) => {
    const largeCategoryID = options.current.corp_large_category_id;
    if (!largeCategoryID) {
      delete options.current.corp_medium_category_id;
    } else {
      const largeCategory = categoryList.find((v) => v.id === largeCategoryID);
      if (!largeCategory) {
        delete options.current.corp_medium_category_id;
      } else {
        const target = largeCategory.list.find((v) => v.id === id);
        if (target) {
          options.current.corp_medium_category_id = id;
        } else {
          delete options.current.corp_medium_category_id;
        }
      }
    }
    delete options.current.page_no;
    delete options.current.sort_order;
    options.current.get_count = PER_PAGE_TYPES.DEFAULT;

    reload();
  };

  const onChangePrefectureCode = async (code?: PrefectureCode) => {
    if (code) {
      options.current.prefecture_cd = code;
    } else {
      delete options.current.prefecture_cd;
    }

    reload();
  };

  const onSearch = async () => {
    delete options.current.page_no;
    delete options.current.sort_order;
    options.current.get_count = PER_PAGE_TYPES.DEFAULT;

    reload();
  };

  const onReset = async () => {
    // options.current.keyword = "";
    // options.current.sort_order = SortOrderTypePublicCorps.UPDARE_DATE_NEW;
    setKeyword("");
    // delete options.current.corp_large_category_id;
    // delete options.current.corp_medium_category_id;
    // delete options.current.prefecture_cd;

    for (let k in options.current) {
      delete (options.current as any)[k];
    }
    options.current.get_count = PER_PAGE_TYPES.DEFAULT;
    reload();
  };

  const onChangeSort = async (value: string) => {
    options.current.sort_order = value;
    delete options.current.page_no;

    reload();
  };

  const onChangePerPage = async () => {
    delete options.current.page_no;

    reload();
  };
  
  const onChangePage = async (value: number) => {
    if (value <= 1) {
      delete options.current.page_no;
    } else {
      options.current.page_no = value;
    }

    reload();
  };

  const onAddCandidate = async (corp: PublicCorpModel, index: number) => {
    console.log("商談候補に追加", corp.corp_name);

    ctx.loading.setState(true);
    try {
      // check item has candidate
      if (corp.candidate_corp_id) {
        await API.CandidateCorp.del(corp.corp_id);
      } else {
        await API.CandidateCorp.add(corp.corp_id);
      }

      let newDataCorp = [...data?.public_corp as PublicCorpModel[]];
      newDataCorp[index] = {
        ...newDataCorp[index],
        candidate_corp_id: corp.corp_id === corp.candidate_corp_id ? null : corp.corp_id
      } as any;

      setData({ ...data, public_corp: newDataCorp } as PublicCorpListModel);
    } catch (err) {
      Utils.errorAction(ctx, err);
    }
    ctx.loading.setState(false);
  };

  React.useEffect(() => {
    if (searchedQuery.current === location.search) {
      console.log("useEffect => load cancel.");
      return;
    }
    console.log("useEffect => load start. : " + location.search);
    searchedQuery.current = location.search;
    const f = async () => {
      ctx.loading.setState(true);
      if (!ctx.auth.data) {
        await ctx.auth.initialize();
      }

      // optionsを一度クリアしてから、クエリの状態に
      for (let k in options.current) {
        delete (options.current as any)[k];
      }
      const query = qs.parse(location.search);
      if (query.page_no !== undefined) {
        options.current.page_no = Number(query.page_no);
      }
      if (query.sort_order !== undefined) {
        options.current.sort_order = String(query.sort_order);
      }
      if (query.corp_large_category_id !== undefined) {
        options.current.corp_large_category_id = String(query.corp_large_category_id);
      }
      if (query.corp_medium_category_id !== undefined) {
        options.current.corp_medium_category_id = String(query.corp_medium_category_id);
      }
      if (query.prefecture_cd !== undefined) {
        options.current.prefecture_cd = String(query.prefecture_cd);
      }
      if (query.keyword !== undefined) {
        options.current.keyword = String(query.keyword);
      }
      if (query.get_count !== undefined) {
        options.current.get_count = Number(query.get_count);
      }

      setKeyword(options.current.keyword ?? "");

      if (categoryList.length === 0) {
        // 会社カテゴリ未ロードならロード
        try {
          const response = await API.CategoryCorp.list();
          console.log(response);
          response.data.unshift({
            name: "すべて",
            id: "",
            type: CategoryType.LARGE,
            list: [],
            sort_no: -1,
          });
          setCategoryList(response.data);
        } catch (err) {
          Utils.errorAction(ctx, err);
        }
      }
      const response = await API.Corp.search(options.current);
      console.log(response);
      setData(response.data);
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });

      ctx.loading.setState(false);
    };
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (!data) {
    return <div></div>;
  }

  return (
    <div className={`flex flex-col h-full`}>
      <CorpListMainCategory value={options.current.corp_large_category_id} data={categoryList} onChange={onChangeLargeCategory} />
      <Helmet>
        <title>会社一覧</title>
      </Helmet>
      <div className="flex-1 flex border-t">
        <CorpListSideMenu
          categoryList={categoryList}
          largeCategoryID={options.current.corp_large_category_id}
          mediumCategoryID={options.current.corp_medium_category_id}
          onChangeMediumCategoryID={onChangeMediumCategoryID}
          prefectureCode={options.current.prefecture_cd}
          onChangePrefectureCode={onChangePrefectureCode}
        />
        <div className="flex-1">
          {/* 検索条件 */}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSearch();
            }}
            className="border-y p-4 flex items-start gap-4 threadAdminSearch"
          >
            <div className="flex-1 flex items-center gap-2">
              <span>キーワード</span>
              <input
                className="flex-1"
                value={keyword}
                onChange={(e) => {
                  const value = e.target.value;
                  options.current.keyword = value;
                  setKeyword(value);
                }}
                maxLength={50}
              />
            </div>
            <div className="flex flex-col">
              <button type="submit" className="primary px-4 py-2" onClick={onSearch}>
                検索
              </button>
              <div className="mt-2 underline cursor-pointer" onClick={onReset}>
                検索条件を解除
              </div>
            </div>
          </form>

          {/* 検索結果 */}
          {data.public_corp.length === 0 ? (
            <div className="flex justify-center py-12">
              <div>検索結果がありません。</div>
            </div>
          ) : (
            <div className="p-4">
              <div className="flex gap-10 mb-2">
                <div>
                  <RowsPerPageSelect options={options.current} onChange={onChangePerPage} />
                  <span>を表示</span>
                </div>
                <div>
                  <span className="mr-4">並び替え</span>
                  <CorpSearchSortSelect value={options.current.sort_order} onChange={onChangeSort} />
                </div>
              </div>
              <PageLabel data={data} />
              {data.public_corp.map((item, index) => (
                <div className="flex border-b py-2 group item-hover" key={index}>
                  <div className="w-3/12 relative" style={{ maxWidth: "250px" }}>
                    <Link to={`/company-detail/${item.corp_id}/`}>
                      <Image data={item.logo_image} />
                    </Link>
                    <div className="absolute right-0 bottom-0 p-2">{item.is_hide && <Chip text="非公開" />}</div>
                  </div>
                  <div className="flex-1 p-4">
                    <Link to={`/company-detail/${item.corp_id}/`} className="hover:underline group-hover:text-jhk-blue">
                      <div className="font-bold mb-4 text-sm break-all line-clamp-1" title={item.corp_name}>{item.corp_name}</div>
                    </Link>
                    <div className="whitespace-pre-line line-clamp-5 break-all" title={item.introduction}>{item.introduction}</div>
                    {!ctx.auth.data?.is_admin_corp && (
                      <>
                        <div className="mt-2 flex gap-4">
                          <button
                            className={`secondary px-4 py-2 item-favorite  ${item.corp_id == item.candidate_corp_id ? 'is-favorite' : ''}`}
                            onClick={() => {
                              onAddCandidate(item, index);
                            }}
                          >
                            {item.corp_id == item.candidate_corp_id ? '商談候補会社に追加済' : '商談候補会社に追加'}
                          </button>
                          <button
                            className="secondary px-4 py-2 item-favorite"
                            onClick={() => {
                              selectedItem.current = item;
                              setIsOpen(true);
                            }}
                          >
                            お問い合わせ
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex justify-center m-4">
                <Pagination page={options.current?.page_no ?? 1} data={data} onChange={onChangePage} />
              </div>
            </div>
          )}
        </div>
      </div>
      <ContactDialog
        ctx={ctx}
        modalIsOpen={modalIsOpen}
        targets={selectedItem.current ? [selectedItem.current] : []}
        close={() => setIsOpen(false)}
      />
    </div>
  );
};
export default CorpListPage;
